import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Search, MapPin, Leaf, Wind, Brain, Hand, Sparkles, ArrowRight, Star, Calendar, Shield, Quote, Target, Mic, Heart, Lock, CreditCard, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTY_ICONS = {
  therapeute: Heart,
  naturopathe: Leaf,
  sophrologue: Wind,
  psychologue: Brain,
  osteopathe: Hand,
  hypnotherapeute: Sparkles,
  coach: Target,
  orthophoniste: Mic,
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, getTestimonials } = useLanguage();
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [city, setCity] = useState('');
  const [featuredPractitioners, setFeaturedPractitioners] = useState([]);

  const testimonials = getTestimonials();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [specResponse, practResponse] = await Promise.all([
          axios.get(`${API}/specialties`),
          axios.get(`${API}/practitioners`)
        ]);
        setSpecialties(specResponse.data);
        setFeaturedPractitioners(practResponse.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (city) params.set('city', city);
    navigate(`/search?${params.toString()}`);
  };

  const getSpecialtyName = (id) => {
    return t(`specialties.${id}`) || id;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />
      
      {/* Apora Section */}
      <section className="bg-white border-b border-sand-200" data-testid="apora-section">
        <div className="container-app py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            {/* Apora Image */}
            <div className="flex-shrink-0">
              <img 
                src="/apora.jpg" 
                alt="Apora - Muse des vergers enchantés" 
                className="w-32 h-32 md:w-40 md:h-40 object-contain"
              />
            </div>
            
            {/* Poem Text */}
            <div className="text-center md:text-left">
              <p className="text-sm md:text-base text-foreground leading-relaxed italic font-serif">
                Ô douce Opora, muse des vergers enchantés,<br />
                Toi qui dans tes mains tiens la promesse dorée,<br />
                De chaque fleur fragile, tu fais éclore la vie,<br />
                Offrant au monde entier tes fruits de poésie.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="hero-pattern relative overflow-hidden" data-testid="hero-section">
        <div className="container-app py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage-100 rounded-full">
                <Leaf className="w-4 h-4 text-sage-600" />
                <span className="text-sm font-medium text-sage-700">{t('landing.badge')}</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight leading-tight">
                {t('landing.title')} <span className="text-sage-500">{t('landing.titleHighlight')}</span>
              </h1>
              
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                {t('landing.subtitle')}
              </p>

              {/* Search Form */}
              <form onSubmit={handleSearch} className="bg-white p-4 md:p-6 rounded-2xl shadow-soft-lg border border-sand-200" data-testid="search-form">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('landing.searchSpecialty')}</label>
                    <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                      <SelectTrigger className="w-full h-12 rounded-xl border-sand-200" data-testid="specialty-select">
                        <SelectValue placeholder={t('landing.searchSpecialtyPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search.allSpecialties')}</SelectItem>
                        {specialties.map((spec) => (
                          <SelectItem key={spec.id} value={spec.id}>{getSpecialtyName(spec.id)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('landing.searchCity')}</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input 
                        type="text"
                        placeholder={t('landing.searchCityPlaceholder')}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-sand-200"
                        data-testid="city-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-end">
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-sage-500 hover:bg-sage-600 text-white rounded-xl font-medium"
                      data-testid="search-button"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      {t('landing.searchButton')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Right Image */}
            <div className="hidden lg:block relative animate-fadeIn stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg">
                <img 
                  src="/images/hero-bg.jpg"
                  alt="Wellness and nature"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sage-900/20 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-soft-lg border border-sand-200 animate-fadeIn stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-terracotta-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-terracotta-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('landing.easyBooking')}</p>
                    <p className="text-xs text-muted-foreground">{t('landing.easyBookingDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="specialties-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">{t('landing.specialtiesTitle')}</h2>
            <p className="section-subtitle mx-auto">
              {t('landing.specialtiesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {specialties.map((spec, index) => {
              const IconComponent = SPECIALTY_ICONS[spec.id] || Leaf;
              return (
                <div
                  key={spec.id}
                  onClick={() => navigate(`/search?specialty=${spec.id}`)}
                  className={`specialty-card text-center animate-fadeIn stagger-${index + 1}`}
                  data-testid={`specialty-${spec.id}`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-sage-600" />
                  </div>
                  <h3 className="font-heading font-medium text-foreground">{getSpecialtyName(spec.id)}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Practitioners */}
      {featuredPractitioners.length > 0 && (
        <section className="py-16 md:py-24 bg-sand-100" data-testid="featured-practitioners">
          <div className="container-app">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <div>
                <h2 className="section-title mb-4">{t('landing.featuredTitle')}</h2>
                <p className="section-subtitle">
                  {t('landing.featuredSubtitle')}
                </p>
              </div>
              <Link to="/search" className="mt-6 md:mt-0">
                <Button variant="ghost" className="text-sage-600 hover:text-sage-700" data-testid="view-all-practitioners">
                  {t('landing.viewAll')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPractitioners.map((practitioner, index) => {
                const IconComponent = SPECIALTY_ICONS[practitioner.specialty] || Leaf;
                return (
                  <Link
                    key={practitioner.id}
                    to={`/practitioner/${practitioner.id}`}
                    className={`practitioner-card animate-fadeIn stagger-${index + 1}`}
                    data-testid={`practitioner-card-${practitioner.id}`}
                  >
                    <div className="aspect-[4/3] relative">
                      <img 
                        src={practitioner.photo_url 
                          ? (practitioner.photo_url.startsWith('/api/photos') || practitioner.photo_url.startsWith('/uploads')
                              ? `${process.env.REACT_APP_BACKEND_URL}${practitioner.photo_url}`
                              : practitioner.photo_url)
                          : `/images/default-practitioner.jpg`
                        }
                        alt={`${practitioner.user?.first_name} ${practitioner.user?.last_name}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="badge-specialty">
                          <IconComponent className="w-3 h-3 mr-1" />
                          {getSpecialtyName(practitioner.specialty)}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-lg font-medium text-foreground mb-1">
                        {practitioner.user?.first_name} {practitioner.user?.last_name}
                      </h3>
                      {practitioner.city && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                          <MapPin className="w-4 h-4" />
                          {practitioner.city}
                        </p>
                      )}
                      {practitioner.price && (
                        <p className="text-sage-600 font-medium">
                          {practitioner.price}€ {t('landing.perConsultation')}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="testimonials-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">{t('landing.testimonialTitle')}</h2>
            <p className="section-subtitle mx-auto">
              {t('landing.testimonialSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`bg-sand-50 rounded-2xl p-6 md:p-8 relative animate-fadeIn stagger-${index + 1}`}
                data-testid={`testimonial-${index}`}
              >
                <Quote className="w-10 h-10 text-sage-200 absolute top-6 right-6" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-terracotta-400 text-terracotta-400" />
                  ))}
                </div>
                <p className="text-foreground mb-6 leading-relaxed relative z-10">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-sage-200 rounded-full flex items-center justify-center">
                    <span className="font-heading font-semibold text-sage-700">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-sand-100" data-testid="features-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">{t('landing.whyChooseTitle')}</h2>
            <p className="section-subtitle mx-auto">
              {t('landing.whyChooseSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center animate-fadeIn stagger-1">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100 rounded-2xl flex items-center justify-center">
                <Search className="w-8 h-8 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">{t('landing.feature1Title')}</h3>
              <p className="text-muted-foreground">
                {t('landing.feature1Desc')}
              </p>
            </div>
            
            <div className="text-center animate-fadeIn stagger-2">
              <div className="w-16 h-16 mx-auto mb-6 bg-terracotta-100 rounded-2xl flex items-center justify-center">
                <Calendar className="w-8 h-8 text-terracotta-500" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">{t('landing.feature2Title')}</h3>
              <p className="text-muted-foreground">
                {t('landing.feature2Desc')}
              </p>
            </div>
            
            <div className="text-center animate-fadeIn stagger-3">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground mb-3">{t('landing.feature3Title')}</h3>
              <p className="text-muted-foreground">
                {t('landing.feature3Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-sage-500" data-testid="cta-section">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaSubtitle')}
          </p>
          <Link to="/auth?tab=register&role=practitioner">
            <Button className="bg-white text-sage-600 hover:bg-sand-100 rounded-full px-8 py-6 text-lg font-medium shadow-lg" data-testid="cta-practitioner">
              {t('landing.ctaButton')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-12 bg-sand-50 border-t border-sand-200">
        <div className="container-app">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">{t('landing.gdprCompliant') || 'GDPR Compliant'}</p>
                <p className="text-xs">{t('landing.dataProtection') || 'Your data is protected'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">{t('landing.securePayments') || 'Secure Payments'}</p>
                <p className="text-xs">{t('landing.poweredByStripe') || 'Powered by Stripe'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-sage-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">{t('landing.sslEncrypted') || 'SSL Encrypted'}</p>
                <p className="text-xs">{t('landing.secureConnection') || 'Secure connection'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white" data-testid="footer">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-sage-500 rounded-xl flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold">Opora Vie</span>
            </div>
            <p className="text-sand-300 text-sm">
              © 2024 Opora Vie. {t('landing.allRights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
